import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { usePortalRole } from '../../contexts/PortalRoleContext';
import {
  listStudentBillingSummaries,
  getStudentBillingSummary
} from '../../api/studentBillingSummaryApi';
import { listCampuses } from '../../api/campusPortalApi';
import * as S from '../../pages/Portal/Management.styles';

const StudentBillingSummary = () => {
  const auth = useAuth();
  const { currentUserCampus } = usePortalRole();
  const userGroups = auth.user?.profile['cognito:groups'] || [];
  const portalGroups = userGroups.filter(g => g.startsWith('portal_'));

  // Permission checks
  const isDeveloper = portalGroups.includes('portal_developer');
  const isDirector = portalGroups.includes('portal_director');
  const isSystemAdmin = portalGroups.includes('portal_system_admin');
  const isManager = portalGroups.includes('portal_manager');
  const canView = isDeveloper || isDirector || isSystemAdmin || isManager || portalGroups.includes('portal_admin');
  const canFilterCampus = isDeveloper || isSystemAdmin;

  // Data states
  const [summaries, setSummaries] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [managerCampusId, setManagerCampusId] = useState(null);

  // Filter states
  const [filterCampus, setFilterCampus] = useState(currentUserCampus || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name, balance, purchased

  // Update campus filter when currentUserCampus becomes available (async profile load)
  useEffect(() => {
    if (currentUserCampus && !canFilterCampus) {
      setFilterCampus(currentUserCampus);
    }
  }, [currentUserCampus, canFilterCampus]);

  // Detail modal states
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!canView) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [summariesResult, campusesData] = await Promise.all([
        listStudentBillingSummaries({ campusId: filterCampus !== 'all' ? filterCampus : undefined }),
        listCampuses()
      ]);
      setSummaries(summariesResult.summaries);
      setCampuses(campusesData);
      if (summariesResult.requesterCampusId) {
        setManagerCampusId(summariesResult.requesterCampusId);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [canView, filterCampus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch student detail
  const handleViewDetail = async (summary) => {
    setSelectedStudent(summary);
    setLoadingDetail(true);
    try {
      const detail = await getStudentBillingSummary(summary.studentId);
      setStudentDetail(detail);
    } catch (err) {
      console.error('Error fetching student detail:', err);
      setStudentDetail(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  // Close detail modal
  const handleCloseDetail = () => {
    setSelectedStudent(null);
    setStudentDetail(null);
  };

  // Filter and sort summaries
  const filteredSummaries = summaries
    .filter(s => {
      if (searchTerm) {
        return s.studentName.toLowerCase().includes(searchTerm.toLowerCase());
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'balance':
          return b.studentBalance - a.studentBalance;
        case 'purchased':
          return b.totalPurchased - a.totalPurchased;
        case 'name':
        default:
          return a.studentName.localeCompare(b.studentName);
      }
    });

  // Calculate totals
  const totalBalance = filteredSummaries.reduce((sum, s) => sum + s.studentBalance, 0);
  const totalPurchased = filteredSummaries.reduce((sum, s) => sum + s.totalPurchased, 0);
  const totalCourseCredits = filteredSummaries.reduce((sum, s) => sum + s.totalCourseCredits, 0);

  if (auth.isLoading || loading) {
    return (
      <S.Card>
        <S.CardHeader>
          <h3>Student Billing Summary</h3>
        </S.CardHeader>
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
          Loading...
        </div>
      </S.Card>
    );
  }

  if (!canView) {
    return (
      <S.Card>
        <S.CardHeader>
          <h3>Student Billing Summary</h3>
        </S.CardHeader>
        <S.ErrorBanner style={{ margin: '16px' }}>
          You do not have permission to view billing summaries.
        </S.ErrorBanner>
      </S.Card>
    );
  }

  return (
    <>
      <S.Card>
        <S.CardHeader>
          <h3>Student Billing Summary</h3>
        </S.CardHeader>

        {error && (
          <S.ErrorBanner style={{ margin: '16px' }}>
            Error: {error}
          </S.ErrorBanner>
        )}

        {/* Summary Stats */}
        <S.StatsGrid style={{ padding: '16px', gap: '16px' }}>
          <S.StatCard color="#10b981" style={{ padding: '16px' }}>
            <S.StatNumber style={{ fontSize: '24px' }}>${totalBalance.toFixed(2)}</S.StatNumber>
            <S.StatLabel>Total Student Balance</S.StatLabel>
          </S.StatCard>
          <S.StatCard color="#3b82f6" style={{ padding: '16px' }}>
            <S.StatNumber style={{ fontSize: '24px' }}>${totalPurchased.toFixed(2)}</S.StatNumber>
            <S.StatLabel>Total Purchased</S.StatLabel>
          </S.StatCard>
          <S.StatCard color="#F1AF59" style={{ padding: '16px' }}>
            <S.StatNumber style={{ fontSize: '24px' }}>${totalCourseCredits.toFixed(2)}</S.StatNumber>
            <S.StatLabel>Total Course Credits</S.StatLabel>
          </S.StatCard>
          <S.StatCard color="#6b7280" style={{ padding: '16px' }}>
            <S.StatNumber style={{ fontSize: '24px' }}>{filteredSummaries.length}</S.StatNumber>
            <S.StatLabel>Total Students</S.StatLabel>
          </S.StatCard>
        </S.StatsGrid>

        {/* Filters */}
        <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <S.SearchInput
            type="text"
            placeholder="Search by student name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: '200px' }}
          />
          {canFilterCampus && (
            <S.FilterSelect
              value={filterCampus}
              onChange={(e) => setFilterCampus(e.target.value)}
            >
              <option value="all">All Campuses</option>
              {campuses.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </S.FilterSelect>
          )}
          {isManager && managerCampusId && (
            <S.Badge color="#6b7280" style={{ alignSelf: 'center' }}>
              Your Campus Only
            </S.Badge>
          )}
          <S.FilterSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Sort by Name</option>
            <option value="balance">Sort by Balance</option>
            <option value="purchased">Sort by Purchased</option>
          </S.FilterSelect>
        </div>

        {/* Table */}
        <S.TableContainer style={{ boxShadow: 'none' }}>
          <S.Table>
            <thead>
              <tr>
                <S.Th>Student</S.Th>
                <S.Th>Balance</S.Th>
                <S.Th>Total Purchased</S.Th>
                <S.Th>Course Credits</S.Th>
                <S.Th>Enrollments</S.Th>
                <S.Th>Campuses</S.Th>
                <S.Th>Actions</S.Th>
              </tr>
            </thead>
            <tbody>
              {filteredSummaries.length === 0 ? (
                <tr>
                  <S.Td colSpan={7} style={{ textAlign: 'center', color: '#6b7280' }}>
                    No students found
                  </S.Td>
                </tr>
              ) : (
                filteredSummaries.map(summary => (
                  <tr key={summary.studentId}>
                    <S.Td>
                      <S.UserName>{summary.studentName}</S.UserName>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {summary.status === 'inactive' && <S.Badge color="#ef4444">Inactive</S.Badge>}
                      </div>
                    </S.Td>
                    <S.Td>
                      <S.Badge color={summary.studentBalance > 0 ? '#10b981' : '#6b7280'}>
                        ${summary.studentBalance.toFixed(2)}
                      </S.Badge>
                    </S.Td>
                    <S.Td>${summary.totalPurchased.toFixed(2)}</S.Td>
                    <S.Td>${summary.totalCourseCredits.toFixed(2)}</S.Td>
                    <S.Td>{summary.enrollmentCount}</S.Td>
                    <S.Td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {summary.campuses.map((campus, idx) => (
                          <S.Badge key={idx} color="#3b82f6" style={{ fontSize: '11px', padding: '2px 8px' }}>
                            {campus}
                          </S.Badge>
                        ))}
                      </div>
                    </S.Td>
                    <S.Td>
                      <S.ActionButton color="#F1AF59" onClick={() => handleViewDetail(summary)}>
                        View Details
                      </S.ActionButton>
                    </S.Td>
                  </tr>
                ))
              )}
            </tbody>
          </S.Table>
        </S.TableContainer>

        <S.Stats>
          Showing {filteredSummaries.length} of {summaries.length} students
        </S.Stats>
      </S.Card>

      {/* Detail Modal */}
      {selectedStudent && (
        <S.ModalOverlay>
          <S.Modal style={{ maxWidth: '700px' }}>
            <S.ModalHeader>
              <h3>Billing Details: {selectedStudent.studentName}</h3>
              <S.CloseButton onClick={handleCloseDetail}>&times;</S.CloseButton>
            </S.ModalHeader>

            <S.ModalContent>
              {loadingDetail ? (
                <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                  Loading...
                </div>
              ) : studentDetail ? (
                <>
                  {/* Summary Stats */}
                  <S.InfoGrid style={{ background: '#f9fafb', borderRadius: '8px', marginBottom: '16px' }}>
                    <S.InfoItem>
                      <S.InfoLabel>Student Balance</S.InfoLabel>
                      <S.InfoValue style={{ color: '#10b981' }}>
                        ${studentDetail.studentBalance.toFixed(2)}
                      </S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Total Purchased</S.InfoLabel>
                      <S.InfoValue>${studentDetail.totalPurchased.toFixed(2)}</S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Total Refunded</S.InfoLabel>
                      <S.InfoValue style={{ color: '#ef4444' }}>
                        ${studentDetail.totalRefunded.toFixed(2)}
                      </S.InfoValue>
                    </S.InfoItem>
                    <S.InfoItem>
                      <S.InfoLabel>Credits Used</S.InfoLabel>
                      <S.InfoValue>${studentDetail.totalCreditsUsed.toFixed(2)}</S.InfoValue>
                    </S.InfoItem>
                  </S.InfoGrid>

                  {/* Course Enrollments */}
                  <h4 style={{ margin: '16px 0 8px', fontSize: '14px', fontWeight: '600' }}>
                    Course Enrollments ({studentDetail.enrollments.length})
                  </h4>
                  {studentDetail.enrollments.length > 0 ? (
                    <S.TableContainer style={{ boxShadow: 'none', marginBottom: '16px' }}>
                      <S.Table>
                        <thead>
                          <tr>
                            <S.Th>Course</S.Th>
                            <S.Th>Credit</S.Th>
                            <S.Th>Used</S.Th>
                            <S.Th>Remaining</S.Th>
                            <S.Th>Discount</S.Th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentDetail.enrollments.map((e, idx) => (
                            <tr key={idx}>
                              <S.Td>{e.courseGroupName}</S.Td>
                              <S.Td>${e.credit.toFixed(2)}</S.Td>
                              <S.Td>${e.totalCreditsUsed.toFixed(2)}</S.Td>
                              <S.Td>
                                <S.Badge color={e.remainingCredits >= 0 ? '#10b981' : '#ef4444'}>
                                  ${e.remainingCredits.toFixed(2)}
                                </S.Badge>
                              </S.Td>
                              <S.Td>{e.discount > 0 ? `${e.discount}%` : '-'}</S.Td>
                            </tr>
                          ))}
                        </tbody>
                      </S.Table>
                    </S.TableContainer>
                  ) : (
                    <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center', background: '#f9fafb', borderRadius: '8px', marginBottom: '16px' }}>
                      No course enrollments
                    </div>
                  )}

                  {/* Payment History */}
                  <h4 style={{ margin: '16px 0 8px', fontSize: '14px', fontWeight: '600' }}>
                    Recent Transactions ({studentDetail.paymentHistory.length})
                  </h4>
                  {studentDetail.paymentHistory.length > 0 ? (
                    <S.TableContainer style={{ boxShadow: 'none' }}>
                      <S.Table>
                        <thead>
                          <tr>
                            <S.Th>Date</S.Th>
                            <S.Th>Amount</S.Th>
                            <S.Th>Description</S.Th>
                            <S.Th>Type</S.Th>
                          </tr>
                        </thead>
                        <tbody>
                          {studentDetail.paymentHistory.map((p, idx) => (
                            <tr key={idx}>
                              <S.Td>{new Date(p.date).toLocaleDateString()}</S.Td>
                              <S.Td>
                                <S.Badge color={p.amount >= 0 ? '#10b981' : '#ef4444'}>
                                  {p.amount >= 0 ? '+' : ''}${p.amount.toFixed(2)}
                                </S.Badge>
                              </S.Td>
                              <S.Td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {p.description}
                              </S.Td>
                              <S.Td>
                                <S.Badge color="#3b82f6" style={{ fontSize: '11px' }}>
                                  {p.transferType === 'normal' ? 'External' :
                                   p.transferType === 'from_student' ? 'From Balance' :
                                   p.transferType === 'term_settlement' ? 'Term Settlement' : 'To Balance'}
                                </S.Badge>
                              </S.Td>
                            </tr>
                          ))}
                        </tbody>
                      </S.Table>
                    </S.TableContainer>
                  ) : (
                    <div style={{ padding: '16px', color: '#6b7280', textAlign: 'center', background: '#f9fafb', borderRadius: '8px' }}>
                      No transactions found
                    </div>
                  )}
                </>
              ) : (
                <div style={{ padding: '24px', color: '#ef4444', textAlign: 'center' }}>
                  Failed to load student details
                </div>
              )}
            </S.ModalContent>

            <S.ModalFooter>
              <S.Button color="#6b7280" onClick={handleCloseDetail}>
                Close
              </S.Button>
            </S.ModalFooter>
          </S.Modal>
        </S.ModalOverlay>
      )}
    </>
  );
};

export default StudentBillingSummary;
